import React, { FC, PropsWithChildren } from 'react';
import { ImageSlider } from './components/Slider/ImageSlider';
import { Grid } from '@/components';
import { useTranslate } from '@/i18n/useTranslate';
import { useInitVivicaLoader } from '@/hooks/useVivicaLoader';
import {
  Box,
  GlobalStyles,
  Stack,
  styled,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import bannerImage from './banner.svg';
import AppLogo from '@/images/logo.svg?react';
import { useMQuery } from '@/hooks/useMQuery';
import { globalStyles } from '@/features/layouts/AuthLayout/globalStyles';

const PageContainer = styled('div')(({ theme: t }) => ({
  maxWidth: 1184,
  margin: '0 auto',
  padding: '0 36px',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [t.breakpoints.down('lg')]: {
    padding: '0 24px',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
}));
const FormLayout = styled('div')`
  min-height: 792px;
  display: flex;
  align-items: stretch;
  width: 100%;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    min-height: auto;
    flex: 1 1 auto;
    padding-bottom: 24px;
    padding-top: 24px;
  }
`;
const TopBlock = styled('div')({
  height: 80,
  background: `no-repeat url(${bannerImage}) calc(100% - 32px) top white`,
  alignItems: 'center',
  display: 'flex',
});
const Form = styled('div')`
  background-color: ${({ theme }) => theme.palette.common.white};
  flex: 1 1 auto;
  padding: 36px 36px 36px 36px;
  position: relative;
  display: flex;
  .mobile-layout & {
    padding: 24px;
  }
`;
const MobileLayout = styled('div')`
  width: 100%;
  max-width: 786px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
`;

export const AuthLayout: FC<PropsWithChildren> = ({ children }) => {
  const { ready } = useTranslate('auth');
  useInitVivicaLoader(ready);
  const theme = useTheme();
  const { mobile, tablet } = useMQuery();
  return (
    <>
      <GlobalStyles styles={globalStyles} />
      <PageContainer>
        <FormLayout>
          {mobile || tablet ? (
            <MobileLayout>
              <TopBlock sx={{ mb: { xs: 6, sm: 12 } }}>
                <Box component={AppLogo} sx={styles.logo} />
              </TopBlock>
              <Form>{children}</Form>
            </MobileLayout>
          ) : (
            <Grid container spacing={0} sx={{ flex: '1 1 auto' }}>
              <Grid xs={6} sx={{ height: '100%' }}>
                <ImageSlider />
              </Grid>
              <Grid
                xs={6}
                sx={{
                  backgroundColor: theme?.palette.common.white,
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Stack
                  sx={{
                    marginY: 40,
                    maxWidth: 452,
                    position: 'relative',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  {children}
                </Stack>
              </Grid>
            </Grid>
          )}
        </FormLayout>
      </PageContainer>
    </>
  );
};

const styles = {
  logo: {
    path: { fill: (t) => t.palette.primary.main },
    width: 100,
    padding: '0 24px',
  },
} satisfies Record<string, SxProps<Theme>>;
